<template>
  <div class="field has-addons">
    <div class="control is-expanded has-icons-left has-icons-right">
      <input v-model="input"
             :placeholder="placeholder"
             autocomplete="off"
             class="input is-primary is-flex-grow-1"
             :class="{
               'search-input--count-clear': shouldShowFilteredCountText && hasInputText,
               'search-input--count': shouldShowFilteredCountText && !hasInputText,
               'search-input--clear': !shouldShowFilteredCountText && hasInputText
             }"
             data-int="search-input"
             type="text"
             :valueAttr="input"
             @input="handleInput"
             @click="openSuggestions"
             @keydown.up="up"
             @keydown.down="down"
             @keyup.enter="enter" />
      <span class="icon is-small is-left fa fa-search" />
      <ul v-if="hasSuggestions && suggestionsOpen"
          class="menu-list">
        <li v-for="(item, index) in suggestions"
            :key="item.id"
            data-int="search-suggestion">
          <a :class="{'is-active': index === selectedSuggestionIndex}"
             class="search-suggestion"
             @click.stop.prevent="select(item)">
            {{ item }}
          </a>
        </li>
      </ul>
      <span v-if="shouldShowFilteredCountText"
            class="filtered-count-hint"
            :class="{ 'has-clear': hasInputText, 'no-clear': !hasInputText }">
        {{ filteredCountText }}
      </span>
      <span v-if="hasInputText" class="icon is-small is-right fa fa-times clear-button" @click.stop="clear"/>
    </div>
    <slot/>
  </div>
</template>

<script>
import { mapActions, useStore } from 'vuex'
import debounce from 'lodash.debounce'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator'

export default {
  name: 'SearchTypeAhead',
  props: {
    isBom: {
      type: Boolean,
      default: false
    },
    clearOnEnter: Boolean,
    placeholder: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    propertyField: {
      type: String,
      default: 'q'
    },
    content: {
      type: Object,
      default: () => {}
    },
    suggestionTypes: {
      type: Array,
      default: null
    },
    limit: {
      type: Number,
      default: 10
    },
    showFilteredCount: {
      type: Boolean,
      default: false
    },
    totalDisplayed: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      input: this.clearOnEnter || this.$route.name === 'Home' ? '' : this.value || '',
      suggestions: [],
      selectedSuggestionIndex: -1,
      suggestionsDismissed: false,
      suggestionsOpen: false,
      suggestionsRequestId: 0
    }
  },
  setup () {
    const store = useStore()
    const isWidget = store.state.auth.isWidget
    const { currentWidgetView } = useWidgetNavigator()
    return { isWidget, currentWidgetView }
  },
  watch: {
    value (val) {
      this.input = this.clearOnEnter ? '' : val || ''
    },
    homeRoute (updated) {
      if (updated) {
        this.input = ''
      }
    },
    searchQuery (newVal, oldVal) {
      if (!this.isBom) return
      if ((!this.input || this.input === '') && (newVal !== oldVal)) {
        this.input = newVal
      }
    }
  },
  computed: {
    hasSuggestions () {
      return this.suggestions && this.suggestions.length > 0
    },
    homeRoute () {
      return (this.$route.name === 'Home' ||
        (this.isWidget && this.currentWidgetView === 'home'))
    },
    searchQuery () {
      if (!this.isBom) return ''
      return this.$route.query?.q
    },
    hasInputText () {
      return this.input?.trim().length > 1
    },
    shouldShowFilteredCountText () {
      return this.showFilteredCount && this.hasInputText && this.filteredCountText !== ''
    },
    filteredCountText () {
      if (this.totalDisplayed <= this.totalCount && this.totalDisplayed >= 0 && this.totalCount > 0) {
        return this.totalDisplayed
      } else { return '' }
    }
  },
  methods: {
    ...mapActions({
      getSearchSuggestions: 'search/searchSuggestionsWithReturn'
    }),
    /* eslint-disable no-invalid-this */
    debounceInput: debounce(function (e) {
      const term = e.target.value
      if (term?.trim().length) {
        if (!this.suggestionsDismissed) {
          this.getSuggestions()
        }
        if (term?.trim().length >= 2) {
          this.autoEnter(term)
        }
      } else {
        this.suggestions = []
        this.autoEnter('')
      }
    }, 400),
    /* eslint-enable no-invalid-this */
    handleInput (event) {
      this.suggestionsDismissed = false
      this.debounceInput(event)
    },
    openSuggestions () {
      this.suggestionsDismissed = false
      this.selectedSuggestionIndex = -1

      if (this.hasSuggestions) {
        this.suggestionsOpen = true
      } else if (this.input?.trim().length) {
        this.getSuggestions()
      }
    },
    async getSuggestions () {
      const requestId = ++this.suggestionsRequestId
      const suggestions = await this.getSearchSuggestions({
        field: this.propertyField,
        q: this.input,
        content: this.content,
        searchableType: this.suggestionTypes
      })
      if (requestId !== this.suggestionsRequestId) return
      this.suggestions = suggestions ? suggestions.slice(0, this.limit) : []
      this.suggestionsOpen = true
      this.selectedSuggestionIndex = -1
    },
    select (value) {
      this.suggestionsOpen = false
      this.input = value
      this.$emit('enter', value)

      if (this.clearOnEnter || this.$route.name === 'Home') {
        this.input = ''

        const elements = document.getElementsByClassName('search-input')
        for (let i = 0; i < elements.length; i++) { // eslint-disable-line
          elements[i].blur()
        }
      }
    },
    up () {
      this.selectedSuggestionIndex = Math.max(-1, this.selectedSuggestionIndex - 1)
    },
    down () {
      this.selectedSuggestionIndex = Math.min(this.suggestions.length - 1, this.selectedSuggestionIndex + 1)
    },
    enter () {
      if (this.selectedSuggestionIndex >= 0) {
        this.input = this.suggestions[this.selectedSuggestionIndex]
        document.activeElement.blur()
      }
      this.select(this.input)
    },
    autoEnter (value) {
      this.select(value)
    },
    close () {
      this.suggestionsDismissed = true
      this.suggestionsRequestId += 1
      this.selectedSuggestionIndex = -1
      this.suggestionsOpen = false
    },
    closeOnOutsideClick (event) {
      if (!this.$el.contains(event.target)) {
        this.close()
      }
    },
    clear () {
      this.close()
      this.input = ''
      this.suggestions = []
      this.$emit('clearClick')
    }
  },
  mounted () {
    document.addEventListener('click', this.closeOnOutsideClick, true)
  },
  beforeUnmount () {
    document.removeEventListener('click', this.closeOnOutsideClick, true)
    this.suggestionsDismissed = true
    this.suggestionsRequestId += 1
  }
}
</script>

<style scoped>
.is-expanded {
  position: relative;
  z-index: 0;
}
.menu-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  display: block;
  background-color: #fff;
  width: 100%;
  overflow: auto;
  border: 1px solid lightgrey;
  z-index: 2000;
}
input {
  border-width: 2px;
  box-shadow: none;
}

.search-input--count-clear {
  padding-right: 8.25em;
}

.search-input--count {
  padding-right: 6.0em;
}

.search-input--clear {
  padding-right: 3.25em;
}

.clear-button {
  color: black !important;
  cursor: pointer !important;
  pointer-events: visiblepainted !important;
  height: 2.0em !important;
  width: 2.0em !important;
  top: .25em !important;
  right: .25em !important;
}

.filtered-count-hint {
  position: absolute;
  z-index: 5;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  line-height: 1.1;
  padding: 0.35em 0.75em;
  border-radius: 0.45em;
  background-color: var(--info);
  color: var(--info-invert, #fff);
  pointer-events: none;
  white-space: nowrap;
}

.input:focus ~ .filtered-count-hint {
  visibility: visible;
  opacity: 1;
}

.filtered-count-hint.has-clear {
  right: 2.75em;
}

.filtered-count-hint.no-clear {
  right: .75em;
}

.control.has-icons-left:focus-within .icon.is-left {
  display: none;
}

.control.has-icons-left:focus-within .input {
  padding-left: 0.75em;
}
</style>
